import { APOffer } from "@/apps/tatar/activityApp/views/details/offer-feature/APOffer.Interface";
import React from "react";
import i18n from "@/i18n";
import VerticalProcessHistory from "@/components/VerticalProcessHistory/VerticalProcessHistory";
import {
  VerticalProcessEntry,
  VerticalProcessEntryCurrent,
  VerticalProcessEntryFinish,
} from "@/components/VerticalProcessHistory/VerticalProcessHistoryInterfaces";
import { APOfferVacancyStatus, APOfferVacancyMeta } from '../../../model/VacancyApplicant.interface';

type Props = {
  offer: APOffer;
};

const getHistoryInfo = (
  status: APOfferVacancyStatus
): {
  label: string;
  icon: { type: string; data: string };
  backgroundColor: string;
  color: string;
} => {
  switch (status) {
    case "contract_created":
      return {
        label: i18n.t(
          "apTemplate:Vacancy.Applicant.ContractDetails.StatusLabel.contract_created",
          "Mietvertrag Erstellt"
        ),
        icon: {
          type: "light",
          data: "task-checklist-add",
        },
        backgroundColor: "#e6f0fa",
        color: "#1a5aa0",
      };
    case "contract_sent":
      return {
        label: i18n.t(
          "apTemplate:Vacancy.Applicant.ContractDetails.StatusLabel.contract_sent",
          "Mietvertrag versendet"
        ),
        icon: {
          type: "light",
          data: "send-email-monitor",
        },
        backgroundColor: "#e6f0fa",
        color: "#1a5aa0",
      };
    case "signed_partially":
      return {
        label: i18n.t(
          "apTemplate:Vacancy.Applicant.ContractDetails.StatusLabel.signed_partially",
          "Mietvertrag vom Mieter unterschrieben"
        ),
        icon: {
          type: "light",
          data: "signature-sign",
        },
        backgroundColor: "#fff4e5",
        color: "#b26a00",
      };
    case "signed_landlord":
      return {
        label: i18n.t(
          "apTemplate:Vacancy.Applicant.ContractDetails.StatusLabel.signed_landlord",
          "Mietvertrag vom Vermieter unterschrieben"
        ),
        icon: {
          type: "light",
          data: "signature-sign",
        },
        backgroundColor: "#fff4e5",
        color: "#b26a00",
      };
    case "signed":
      return {
        label: i18n.t(
          "apTemplate:Vacancy.Applicant.ContractDetails.StatusLabel.signed",
          "Mietvertrag von allen Mietern unterschrieben"
        ),
        icon: {
          type: "light",
          data: "signature-sign",
        },
        backgroundColor: "#fff4e5",
        color: "#b26a00",
      };
    case "completed":
      return {
        label: i18n.t(
          "apTemplate:Vacancy.Applicant.ContractDetails.StatusLabel.completed",
          "Mietvertrag vom Vermieter unterschrieben"
        ),
        icon: {
          type: "light",
          data: "business-contract-handshake-sign",
        },
        backgroundColor: "#e6f4ea",
        color: "#1f7a4c",
      };
    case "send_failed": {
      return {
        label: i18n.t(
          "apTemplate:Vacancy.Applicant.ContractDetails.StatusLabel.send_failed",
          "Fehler beim Versenden des Vertrags"
        ),
        icon: {
          type: "light",
          data: "server-error-document",
        },
        backgroundColor: "#fdecea",
        color: "#b00020",
      };
    }
    case "revoked": {
      return {
        label: i18n.t(
          "apTemplate:Vacancy.Applicant.ContractDetails.StatusLabel.revoked",
          "Vertrag zurückgezogen"
        ),
        icon: {
          type: "light",
          data: "business-contract-break",
        },
        backgroundColor: "#fef7e0",
        color: "#a05a00",
      };
    }
    case "contract_created_manual": {
      return {
        label: i18n.t(
          "apTemplate:Vacancy.Applicant.ContractDetails.StatusLabel.contract_created_manual",
          "Vertrag als PDF erstellt"
        ),
        icon: {
          type: "light",
          data: "office-file-pdf-1",
        },
        backgroundColor: "#fef7e0",
        color: "#a05a00",
      };
    }
    default:
      return {
        label: "-",
        icon: {
          type: "light",
          data: "real-estate-deal-document",
        },
        backgroundColor: "#e6f4ea",
        color: "#1f7a4c",
      };
  }
};

const VacancyContractStatusHistory = (props: Props) => {
  const { offer } = props;

  const offerMeta = offer.data.meta as APOfferVacancyMeta;
  const statusHistory = offerMeta.statusHistory || [];

  const renderEntries = (): VerticalProcessEntry[] => {
    const statusEntries: VerticalProcessEntry[] = statusHistory.map(
      (history) => {
        const historyInfo = getHistoryInfo(history.status);

        return {
          type: "past",
          date: history.date,
          label: historyInfo.label,
          icon: historyInfo.icon,
          color: historyInfo.color,
          backgroundColor: historyInfo.backgroundColor,
        };
      }
    );

    if (
      statusHistory.length > 0 &&
      statusHistory[statusHistory.length - 1].status === "completed"
    ) {
      const finishEntry: VerticalProcessEntryFinish = {
        type: "finish",
        label: i18n.t(
          "apTemplate:Vacancy.Applicant.ContractDetails.StatusLabel.Finished",
          "Der Vorgang ist abgeschlossen."
        ),
        backgroundColor: "#aac6ab",
        color: "#1a712a",
        icon: {
          type: "light",
          data: "check-1",
        },
      };
      statusEntries.push(finishEntry);
    }

    const lastStatus = statusHistory[statusHistory.length - 1];
    if (
      lastStatus &&
      (lastStatus.status === "contract_sent" ||
        lastStatus.status === "signed_partially")
    ) {
      const currentEntry: VerticalProcessEntryCurrent = {
        type: "current",
        backgroundColor: "#fff4e5",
        color: "#b26a00",
        icon: {
          type: "light",
          data: "hourglass",
        },
        label: i18n.t(
          "apTemplate:Vacancy.Applicant.ContractDetails.StatusLabel.currentWaiting",
          "Mieterunterschrift ausstehend"
        ),
        subLabel: "",
      };

      statusEntries.push(currentEntry);
    }
    if (lastStatus && lastStatus.status === "signed") {
      const currentEntry: VerticalProcessEntryCurrent = {
        type: "current",
        backgroundColor: "#fff4e5",
        color: "#b26a00",
        icon: {
          type: "light",
          data: "hourglass",
        },
        label: i18n.t(
          "apTemplate:Vacancy.Applicant.ContractDetails.StatusLabel.WaitingLandlord",
          "Mieterunterschrift durch Vermieter ausstehend"
        ),
        subLabel: "",
      };

      statusEntries.push(currentEntry);
    }

    if (lastStatus && lastStatus.status === "contract_created") {
      const currentEntry: VerticalProcessEntryCurrent = {
        type: "current",
        backgroundColor: "#fff4e5",
        color: "#b26a00",
        icon: {
          type: "light",
          data: "factory-industrial-robot-arm-1",
        },
        label: i18n.t(
          "apTemplate:Vacancy.Applicant.ContractDetails.StatusLabel.SendContractCurrent",
          "Mietvertrag wird vom System bearbeitet"
        ),
        subLabel: "",
      };
      statusEntries.push(currentEntry);
    }

    if (lastStatus && lastStatus.status === "contract_created_manual") {
      const currentEntry: VerticalProcessEntryCurrent = {
        type: "current",
        backgroundColor: "#fff4e5",
        color: "#b26a00",
        icon: {
          type: "light",
          data: "email-action-send-2",
        },
        label: i18n.t(
          "apTemplate:Vacancy.Applicant.ContractDetails.StatusLabel.SendContractManual",
          "Mietvertrag manuell via Mail versenden"
        ),
        subLabel: "",
      };
      statusEntries.push(currentEntry);
    }
    return statusEntries;
  };

  return (
    <div className="contract-history-container">
      <VerticalProcessHistory
        useMousewheel
        useFreeMode
        data={renderEntries()}
        id={"ap-offer-vacancy-contractstatus"}
      />
    </div>
  );
};

export default VacancyContractStatusHistory;
